import Grid from '@mui/material/Grid2';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import SvgIcon from '@mui/material/SvgIcon';
import { ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import Stack from '@mui/material/Stack';
import { BaseMobileStepper, BaseStepper } from '../../components/base/BStepper';
import { IntegrationCard } from './Shared/components';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore';
import { getActiveStep, getSelectedIntegration } from '../../store/payroll/selectors';
import {
  INTEGRATIONS, INTEGRATIONS_COMPONENTS, PAYROLL_STEPS, INTEGRATIONS_ICONS,
} from './Shared/data';
import { payrollActions } from '../../store/payroll/slice';
import { OrdersCountLoader } from '../orders/Shared/components';

const Payroll = () => {
  const { t } = useTranslation(['common', 'payroll']);
  const selectedIntegration = useAppSelector(getSelectedIntegration);
  const dispatch = useAppDispatch();
  const activeStep = useAppSelector(getActiveStep);
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);

  const renderIntegrationComponent = () => {
    const Component = INTEGRATIONS_COMPONENTS[selectedIntegration.value][activeStep];
    return Component ? <Component /> : <div>Component not found</div>;
  };

  const handleBack = () => {
    if (selectedIntegration.value === 'managemate') {
      dispatch(payrollActions.setActiveStep(activeStep - 2));
    } else {
      dispatch(payrollActions.setActiveStep(activeStep - 1));
    }
  };

  const handleNext = () => {
    if (selectedIntegration.value === 'managemate') {
      dispatch(payrollActions.setActiveStep(2));
    } else dispatch(payrollActions.setActiveStep(activeStep + 1));
  };

  const StepperComponent = isMobile ? BaseMobileStepper : BaseStepper;

  return (
    <StepperComponent
      isNextDisabled={!selectedIntegration?.value}
      activeStep={activeStep}
      steps={PAYROLL_STEPS}
      handleNext={handleNext}
      handleBack={handleBack}
    >
      <Container
        maxWidth={false}
        sx={{
          marginTop: activeStep > 0 ? 0 : 4,
          px: { xs: 0, lg: 2 },
        }}
      >
        {activeStep > 0 && (
        <Stack alignItems="center" direction="row" justifyContent="space-between" mb={2} mt={4}>
          <Box alignItems="center" display="flex">
            <Typography alignItems="center" mr={2} variant="h5">{t('selectedIntegration', { ns: 'payroll' })}</Typography>
            {INTEGRATIONS_ICONS[selectedIntegration?.value]?.isSvgIcon
              ? (
                <SvgIcon
                  fontSize="large"
                  component={INTEGRATIONS_ICONS[selectedIntegration?.value]?.icon as ElementType}
                  inheritViewBox
                />
              ) : (
                <img
                  src={INTEGRATIONS_ICONS[selectedIntegration?.value]?.icon as string}
                  width={40}
                  height={40}
                  alt="appLogo"
                />
              )}
          </Box>
          <OrdersCountLoader pt={0} />
        </Stack>
        )}
        {activeStep === 0 && (
          <Box>
            <Grid container spacing={3}>
              {INTEGRATIONS.map((integration) => (
                <IntegrationCard key={integration.id} integration={integration} />
              ))}
            </Grid>
            {!isMobile && (
            <Box mr={4} mt={4} justifyContent="flex-end" sx={{ display: 'flex', flexDirection: 'row' }}>
              <Button
                variant="outlined"
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ mr: 2 }}
              >
                {t('back')}
              </Button>
              <Button disabled={!selectedIntegration?.value} variant="contained" onClick={handleNext}>
                {t('next', { ns: 'payroll' })}
              </Button>
            </Box>
            )}
          </Box>
        )}
        {activeStep > 0 && renderIntegrationComponent()}
      </Container>
    </StepperComponent>
  );
};

export {
  Payroll,
};
